<template>
  <div
    class="modal fade"
    id="assignRewardsDialog"
    tabindex="-1"
    aria-hidden="true"
    ref="modal"
  >
    <div class="modal-dialog modal-dialog-centered assign-rewards-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title flex-grow-1">
            {{ $t('student_rewards.assign_rewards') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="closeDialog"
          ></button>
        </div>

        <div class="modal-body">
          <div v-if="modalStudent">
            <div v-if="loadingPoints" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
              <p class="mt-2 text-muted">{{ $t('student_rewards.loading_points') }}</p>
            </div>

            <div v-else>
              <div class="student-info mb-4 p-3 bg-light rounded">
                <div class="d-flex align-items-center">
                  <div class="me-3">
                    <div class="bg-primary rounded-circle p-2">
                      <i class="fas fa-user-graduate text-white"></i>
                    </div>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-1 fw-bold">{{ modalStudent.full_name }}</h6>
                    <div class="d-flex flex-wrap gap-2">
                      <span class="badge bg-secondary">
                        <i class="fas fa-user me-1"></i>
                        {{ currentUser.name || currentUser.username }}
                      </span>
                      <span class="badge bg-info">
                        <i class="fas fa-graduation-cap me-1"></i>
                        {{ modalStudent.current_assignment?.class?.name || $t('student_rewards.no_class') }}
                      </span>
                      <span class="badge bg-info">
                        <i class="fas fa-users me-1"></i>
                        {{ modalStudent.current_assignment?.group?.name || $t('student_rewards.no_group') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mb-4">
                <div class="col-md-12 mb-3">
                  <label class="form-label fw-semibold">
                    <i class="fas fa-calendar-alt me-2"></i>
                    {{ $t('student_rewards.academic_year') }} *
                  </label>
                  <select
                    class="form-select"
                    v-model="formData.academic_year_id"
                    required
                    :class="{ 'is-invalid': !formData.academic_year_id && formSubmitted }"
                  >
                    <option value="">{{ $t('student_rewards.select_academic_year') }}</option>
                    <option v-for="year in academicYears" :key="year.id" :value="year.id">
                      {{ year.name }}
                    </option>
                  </select>
                  <div v-if="!formData.academic_year_id && formSubmitted" class="invalid-feedback">
                    {{ $t('student_rewards.errors.select_academic_year') }}
                  </div>
                </div>

                <div class="col-md-12 mb-3">
                  <label class="form-label fw-semibold">
                    <i class="fas fa-edit me-2"></i>
                    {{ $t('student_rewards.notes') }}
                  </label>
                  <textarea
                    class="form-control"
                    v-model="formData.notes"
                    rows="2"
                    :placeholder="$t('student_rewards.notes_placeholder')"
                  ></textarea>
                </div>
              </div>

              <div class="points-selection mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="mb-0 fw-semibold">{{ $t('student_rewards.select_points') }}</h6>
                  <div class="badge-group">
                    <span class="badge bg-success me-2">
                      <i class="fas fa-plus-circle me-1"></i>
                      {{ selectedPositivePoints.length }}
                    </span>
                    <span class="badge bg-danger">
                      <i class="fas fa-minus-circle me-1"></i>
                      {{ selectedNegativePoints.length }}
                    </span>
                  </div>
                </div>

                <ul class="nav nav-tabs mb-3" id="pointsTab" role="tablist">
                  <li class="nav-item" role="presentation">
                    <button
                      class="nav-link active"
                      id="positive-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#positive"
                      type="button"
                      role="tab"
                    >
                      <i class="fas fa-plus-circle me-2 text-success"></i>
                      {{ $t('student_rewards.positive_points') }}
                      <span class="badge bg-success ms-2">{{ positivePoints.length }}</span>
                    </button>
                  </li>
                  <li class="nav-item" role="presentation">
                    <button
                      class="nav-link"
                      id="negative-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#negative"
                      type="button"
                      role="tab"
                    >
                      <i class="fas fa-minus-circle me-2 text-danger"></i>
                      {{ $t('student_rewards.negative_points') }}
                      <span class="badge bg-danger ms-2">{{ negativePoints.length }}</span>
                    </button>
                  </li>
                </ul>

                <div class="tab-content" id="pointsTabContent">
                  <div class="tab-pane fade show active" id="positive" role="tabpanel">
                    <div
                      v-if="positivePoints.length === 0"
                      class="text-center py-3 text-muted bg-light rounded"
                    >
                      <i class="fas fa-star fa-2x mb-2"></i>
                      <p class="mb-0">{{ $t('student_rewards.no_positive_points') }}</p>
                    </div>
                    <div class="row g-2" v-else>
                      <div
                        v-for="point in positivePoints"
                        :key="point.id"
                        class="col-6 col-md-4 col-lg-3"
                      >
                        <div
                          class="card h-100 point-card"
                          :class="{ selected: selectedPoints.includes(point.id) }"
                          @click="togglePoint(point.id)"
                        >
                          <div class="card-body p-3 text-center">
                            <div class="mb-2">
                              <i :class="`fas ${point.icon} fa-2x text-success`"></i>
                            </div>
                            <h6 class="card-title mb-1 fw-semibold">{{ point.name }}</h6>
                            <p class="card-text text-muted small mb-2">{{ point.description }}</p>
                            <span class="badge bg-success">+{{ point.points }}</span>
                          </div>
                          <div class="card-footer bg-transparent border-top-0 py-2 text-center">
                            <small class="text-muted">
                              <i
                                class="fas fa-check-circle me-1"
                                v-if="selectedPoints.includes(point.id)"
                              ></i>
                              {{
                                selectedPoints.includes(point.id)
                                  ? $t('student_rewards.selected')
                                  : $t('student_rewards.click_to_select')
                              }}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="tab-pane fade" id="negative" role="tabpanel">
                    <div
                      v-if="negativePoints.length === 0"
                      class="text-center py-3 text-muted bg-light rounded"
                    >
                      <i class="fas fa-exclamation-triangle fa-2x mb-2"></i>
                      <p class="mb-0">{{ $t('student_rewards.no_negative_points') }}</p>
                    </div>
                    <div class="row g-2" v-else>
                      <div
                        v-for="point in negativePoints"
                        :key="point.id"
                        class="col-6 col-md-4 col-lg-3"
                      >
                        <div
                          class="card h-100 point-card"
                          :class="{ selected: selectedPoints.includes(point.id) }"
                          @click="togglePoint(point.id)"
                        >
                          <div class="card-body p-3 text-center">
                            <div class="mb-2">
                              <i :class="`fas ${point.icon} fa-2x text-danger`"></i>
                            </div>
                            <h6 class="card-title mb-1 fw-semibold">{{ point.name }}</h6>
                            <p class="card-text text-muted small mb-2">{{ point.description }}</p>
                            <span class="badge bg-danger">-{{ point.points }}</span>
                          </div>
                          <div class="card-footer bg-transparent border-top-0 py-2 text-center">
                            <small class="text-muted">
                              <i
                                class="fas fa-check-circle me-1"
                                v-if="selectedPoints.includes(point.id)"
                              ></i>
                              {{
                                selectedPoints.includes(point.id)
                                  ? $t('student_rewards.selected')
                                  : $t('student_rewards.click_to_select')
                              }}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="selectedPoints.length > 0" class="mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="mb-0 fw-semibold">
                    {{ $t('student_rewards.selected_points_summary') }}
                  </h6>
                  <span class="badge bg-primary">
                    {{ selectedPoints.length }} {{ $t('student_rewards.selected') }}
                  </span>
                </div>

                <div class="selected-points-list">
                  <div v-if="selectedPositivePoints.length > 0" class="mb-3">
                    <div class="d-flex align-items-center mb-2">
                      <i class="fas fa-plus-circle text-success me-2"></i>
                      <h6 class="mb-0 fw-semibold">
                        {{ $t('student_rewards.selected_positive_points') }}
                      </h6>
                      <span class="badge bg-success ms-2">
                        {{ selectedPositivePoints.length }}
                      </span>
                    </div>
                    <div class="row g-2">
                      <div
                        v-for="point in selectedPositivePoints"
                        :key="point.id"
                        class="col-md-6"
                      >
                        <div class="selected-item bg-light rounded p-3">
                          <div class="d-flex align-items-center justify-content-between">
                            <div class="d-flex align-items-center">
                              <i :class="`fas ${point.icon} text-success me-3`"></i>
                              <div>
                                <strong class="d-block">{{ point.name }}</strong>
                                <small class="text-muted">{{ point.description }}</small>
                              </div>
                            </div>
                            <div class="d-flex align-items-center">
                              <span class="badge bg-success me-2">+{{ point.points }}</span>
                              <button
                                @click="togglePoint(point.id)"
                                class="btn btn-sm btn-link text-danger p-0"
                                :title="$t('student_rewards.remove')"
                              >
                                <i class="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="selectedNegativePoints.length > 0">
                    <div class="d-flex align-items-center mb-2">
                      <i class="fas fa-minus-circle text-danger me-2"></i>
                      <h6 class="mb-0 fw-semibold">
                        {{ $t('student_rewards.selected_negative_points') }}
                      </h6>
                      <span class="badge bg-danger ms-2">
                        {{ selectedNegativePoints.length }}
                      </span>
                    </div>
                    <div class="row g-2">
                      <div
                        v-for="point in selectedNegativePoints"
                        :key="point.id"
                        class="col-md-6"
                      >
                        <div class="selected-item bg-light rounded p-3">
                          <div class="d-flex align-items-center justify-content-between">
                            <div class="d-flex align-items-center">
                              <i :class="`fas ${point.icon} text-danger me-3`"></i>
                              <div>
                                <strong class="d-block">{{ point.name }}</strong>
                                <small class="text-muted">{{ point.description }}</small>
                              </div>
                            </div>
                            <div class="d-flex align-items-center">
                              <span class="badge bg-danger me-2">-{{ point.points }}</span>
                              <button
                                @click="togglePoint(point.id)"
                                class="btn btn-sm btn-link text-danger p-0"
                                :title="$t('student_rewards.remove')"
                              >
                                <i class="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="total-summary">
                <div class="card" :class="getTotalPointsCardClass()">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1 fw-semibold">
                          <i class="fas fa-calculator me-2"></i>
                          {{ $t('student_rewards.total_summary') }}
                        </h6>
                        <small class="text-muted">
                          {{ selectedPositivePoints.length }} {{ $t('student_rewards.positive') }},
                          {{ selectedNegativePoints.length }} {{ $t('student_rewards.negative') }}
                        </small>
                      </div>
                      <div class="text-end">
                        <div class="h3 mb-0" :class="getTotalPointsTextClass()">
                          {{ calculateTotalPoints() }}
                        </div>
                        <small class="text-muted">{{ $t('student_rewards.points') }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="validationError" class="alert alert-danger mt-3">
                <i class="fas fa-exclamation-triangle me-2"></i>
                {{ validationError }}
              </div>
            </div>
          </div>

          <div v-else class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ $t('common.loading') }}</span>
            </div>
            <p class="mt-2 text-muted">{{ $t('student_rewards.loading_dialog') }}</p>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-primary"
            @click="handleConfirm"
            :disabled="loading"
          >
            <span v-if="loading">
              <i class="fas fa-spinner fa-spin me-2"></i>
            </span>
            {{ $t('common.save') }}
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            data-bs-dismiss="modal"
            @click="closeDialog"
            :disabled="loading"
          >
            {{ $t('baseDialog.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps, defineExpose, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import { Modal } from 'bootstrap'
import api from '@/api'

const { t } = useI18n()
const toast = useToast()
const modal = ref(null)
let bsModal = null

const props = defineProps({
  student: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['rewards-assigned'])

const loading = ref(false)
const loadingPoints = ref(false)
const formSubmitted = ref(false)
const validationError = ref('')
const modalStudent = ref(null)
const positivePoints = ref([])
const negativePoints = ref([])
const academicYears = ref([])
const currentUser = ref({})

const selectedPoints = ref([])
const formData = ref({
  academic_year_id: '',
  notes: ''
})

const selectedPositivePoints = computed(() => {
  return positivePoints.value.filter(point => selectedPoints.value.includes(point.id))
})

const selectedNegativePoints = computed(() => {
  return negativePoints.value.filter(point => selectedPoints.value.includes(point.id))
})

const getCurrentUser = () => {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    try {
      currentUser.value = JSON.parse(userStr)
    } catch (e) {
      console.error('Error parsing user from localStorage:', e)
      currentUser.value = {}
    }
  }
}

const resetForm = () => {
  formSubmitted.value = false
  validationError.value = ''
  loadingPoints.value = false
  selectedPoints.value = []

  if (modalStudent.value) {
    formData.value = {
      academic_year_id: modalStudent.value.current_academic_year_id || '',
      notes: ''
    }
  } else {
    formData.value = {
      academic_year_id: '',
      notes: ''
    }
  }
}

const fetchRewardPoints = async () => {
  try {
    console.log('Fetching reward points...')
    const [positiveRes, negativeRes] = await Promise.all([
      api.get('/reward-points?type=positive&is_active=true'),
      api.get('/reward-points?type=negative&is_active=true')
    ])

    positivePoints.value = positiveRes.data.data
    negativePoints.value = negativeRes.data.data
    console.log(`Fetched ${positivePoints.value.length} positive and ${negativePoints.value.length} negative points`)
  } catch (error) {
    console.error('Error fetching reward points:', error)
    throw error
  }
}

const fetchAcademicYears = async () => {
  try {
    console.log('Fetching academic years...')
    const response = await api.get('/academic-years')
    academicYears.value = response.data.data

    if (!formData.value.academic_year_id && academicYears.value.length > 0) {
      const currentYear = academicYears.value.find(y => y.is_active)
      if (currentYear) {
        formData.value.academic_year_id = currentYear.id
        console.log('Auto-selected academic year:', currentYear.id)
      }
    }

    console.log('Fetched academic years:', academicYears.value.length)
  } catch (error) {
    console.error('Error fetching academic years:', error)
    throw error
  }
}

const fetchDialogData = async () => {
  if (!modalStudent.value) {
    console.error('Cannot fetch dialog data: no student')
    return
  }

  console.log('Fetching dialog data for student:', modalStudent.value.id)
  loadingPoints.value = true

  try {
    await Promise.all([
      fetchRewardPoints(),
      fetchAcademicYears()
    ])

    console.log('Dialog data fetched successfully')
  } catch (error) {
    console.error('Error fetching dialog data:', error)
    toast.error(t('student_rewards.errors.load_data_failed'))
  } finally {
    loadingPoints.value = false
  }
}

const openDialog = (student = null) => {
  console.log('Opening dialog with student:', student)
  modalStudent.value = student || props.student

  if (!modalStudent.value) {
    console.error('No student provided to modal')
    toast.error(t('student_rewards.errors.no_student_selected'))
    return
  }

  getCurrentUser()
  resetForm()
  bsModal?.show()
  fetchDialogData()
}

const closeDialog = () => {
  bsModal?.hide()
}

const togglePoint = (pointId) => {
  const index = selectedPoints.value.indexOf(pointId)
  if (index === -1) {
    selectedPoints.value.push(pointId)
  } else {
    selectedPoints.value.splice(index, 1)
  }
}

const calculateTotalPoints = () => {
  let total = 0

  selectedPositivePoints.value.forEach(point => {
    total += point.points
  })

  selectedNegativePoints.value.forEach(point => {
    total -= point.points
  })

  return total
}

const getTotalPointsTextClass = () => {
  const total = calculateTotalPoints()
  if (total > 0) return 'text-success'
  if (total < 0) return 'text-danger'
  return 'text-info'
}

const getTotalPointsCardClass = () => {
  const total = calculateTotalPoints()
  if (total > 0) return 'border-success'
  if (total < 0) return 'border-danger'
  return 'border-info'
}

const validateForm = () => {
  formSubmitted.value = true
  validationError.value = ''

  if (!formData.value.academic_year_id) {
    validationError.value = t('student_rewards.errors.select_academic_year')
    return false
  }

  if (!modalStudent.value.current_assignment?.class?.id || !modalStudent.value.current_assignment?.group?.id) {
    validationError.value = t('student_rewards.errors.student_not_assigned')
    return false
  }

  if (selectedPoints.value.length === 0) {
    validationError.value = t('student_rewards.errors.select_at_least_one_point')
    return false
  }

  return true
}

const handleConfirm = async () => {
  if (!validateForm()) {
    return
  }

  loading.value = true
  try {
    const payload = {
      student_id: modalStudent.value.id,
      reward_point_ids: selectedPoints.value,
      notes: formData.value.notes,
      academic_year_id: formData.value.academic_year_id,
      class_id: modalStudent.value.current_assignment.class.id,
      class_group_id: modalStudent.value.current_assignment.group.id
    }

    await api.post('/student-rewards/assign', payload)

    emit('rewards-assigned')
    closeDialog()
  } catch (error) {
    if (error.response?.data?.errors) {
      const errorMessages = Object.values(error.response.data.errors).flat()
      validationError.value = errorMessages.join(', ')
    } else {
      validationError.value = t('student_rewards.errors.assign_failed')
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (modal.value) {
    bsModal = new Modal(modal.value)

    modal.value.addEventListener('hidden.bs.modal', () => {
      validationError.value = ''
    })

    modal.value.addEventListener('shown.bs.modal', () => {
      if (!positivePoints.value.length || !academicYears.value.length) {
        fetchDialogData()
      }
    })
  }
})

onBeforeUnmount(() => {
  if (bsModal) {
    bsModal.dispose()
    bsModal = null
  }
})

getCurrentUser()

defineExpose({ openDialog, closeDialog })
</script>

<style scoped>
.assign-rewards-modal {
  max-width: 1200px;
  width: 95%;
}

.point-card {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 2px solid transparent;
}

.point-card:hover {
  transform: translateY(-2px);
}

.point-card.selected {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.15);
}

.selected-item {
  border: 1px solid #e9ecef;
}
</style>